import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getPoiResources, generateQuiz, getQuiz, startAudioGeneration, checkAudioStatus } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle} from '@/components/ui/alert-dialog';
import { Compass, Loader2, MapPin, Clock, Star, CheckCircle, XCircle, Sparkles, Volume2, Play, Plus, Baby, Music, Wand2, Timer, User, Trash2, Archive, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '@/lib/api';
import { AudioPlayer } from '@/components/AudioPlayer';

const CAT_COLORS = { museum: '#3A86FF', park: '#00CC99', monument: '#F59E0B', restaurant: '#F43F5E', beach: '#06B6D4', market: '#8B5CF6' };

// Duration modes
const DURATION_MODES = {
  short: { label: 'Court', duration: '~3 min', icon: '⚡' },
  standard: { label: 'Moyen', duration: '~5 min', icon: '📖' },
  deep: { label: 'Complet', duration: '7-10 min', icon: '🎓' }
};

export const VisitCompanion = ({ tripId, trip }) => {
  const { user } = useAuth();
  const [pois, setPois] = useState([]);
  const [quiz, setQuiz] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quizLoading, setQuizLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [tab, setTab] = useState('guides');
  
  // Audio states
  const [audioData, setAudioData] = useState({});
  const [generatingPois, setGeneratingPois] = useState({});
  
  // Floating player state
  const [playerData, setPlayerData] = useState(null);
  
  // New guide form
  const [addOpen, setAddOpen] = useState(false);
  const [newGuide, setNewGuide] = useState({ title: '', location: '', category: 'monument', description: '' });
  const [addLoading, setAddLoading] = useState(false);
  
  // Duration selection dialog
  const [durationDialog, setDurationDialog] = useState({ open: false, poiId: null, poi: null });
  
  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, poiId: null, title: '' });
  
  // Show archived toggle
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    loadPois();
    getQuiz(tripId).then(r => setQuiz(r.data)).catch(() => {});
  }, [tripId]);

  const loadPois = async () => {
    try {
      const r = await getPoiResources(tripId);
      const poisData = r.data;
      setPois(poisData);
      
      // Load saved audio data from POI resources
      const savedAudio = {};
      const userLang = user?.language || 'fr';
      poisData.forEach(poi => {
        if (poi.is_archived) return; // Skip archived for audio data
        // Check for saved audio - try different modes and languages
        for (const mode of ['short', 'standard', 'deep']) {
          for (const audience of ['adult', 'kids']) {
            // Try user's language first, then fallback to fr
            for (const lang of [userLang, 'fr']) {
              const key = `audio_guide_${mode}_${lang}_${audience}`;
              if (poi[`${key}_audio`]) {
                const existingKey = savedAudio[poi.poi_id];
                // Prefer adult audio if exists, or take the first one found
                if (!existingKey || audience === 'adult') {
                  savedAudio[poi.poi_id] = {
                    text: poi[`${key}_text`] || '',
                    audioUrl: poi[`${key}_audio`],
                    audience: audience,
                    mode: mode,
                    language: lang,
                    provider: poi[`${key}_provider`] || 'unknown',
                    hasAmbiance: poi[`${key}_has_ambiance`] || false
                  };
                  break; // Found for this mode/audience, no need to check other langs
                }
              }
            }
          }
        }
      });
      
      if (Object.keys(savedAudio).length > 0) {
        setAudioData(prev => ({ ...prev, ...savedAudio }));
      }
    } catch (e) {
      console.error('Error loading POIs:', e);
    }
    setLoading(false);
  };

  const handleGenerateQuiz = async () => {
    setQuizLoading(true);
    try {
      const res = await generateQuiz(tripId);
      setQuiz(res.data); 
      toast.success('Quiz généré !');
    } catch { toast.error('Erreur'); }
    setQuizLoading(false);
  };

  const handleAnswer = (quizId, answer, correct) => {
    setAnswers(p => ({ ...p, [quizId]: { answer, isCorrect: answer === correct } }));
  };

  // Poll for audio generation status
  const pollGenerationStatus = useCallback(async (poiId) => {
    const maxAttempts = 180; // 3 minutes max
    let attempts = 0;
    
    const poll = async () => {
      if (attempts >= maxAttempts) {
        setGeneratingPois(p => {
          const copy = { ...p };
          delete copy[poiId];
          return copy;
        });
        toast.error('Génération timeout');
        return;
      }
      
      try {
        const res = await checkAudioStatus(poiId);
        
        if (res.data.status === 'completed' && res.data.result) {
          setAudioData(p => ({
            ...p,
            [poiId]: {
              text: res.data.result.guide_text,
              audioUrl: res.data.result.audio_url,
              audience: res.data.result.audience,
              mode: res.data.result.mode,
              hasAmbiance: res.data.result.has_ambiance,
              hasTransitions: res.data.result.has_transitions,
              provider: res.data.result.tts_provider
            }
          }));
          setGeneratingPois(p => {
            const copy = { ...p };
            delete copy[poiId];
            return copy;
          });
          
          const hasEffects = res.data.result.has_ambiance || res.data.result.has_transitions;
          toast.success(hasEffects ? 'Audio immersif prêt ! 🎧' : 'Guide audio prêt !');
          return;
        } else if (res.data.status === 'error') {
          setGeneratingPois(p => {
            const copy = { ...p };
            delete copy[poiId];
            return copy;
          });
          toast.error('Erreur génération');
          return;
        } else {
          setGeneratingPois(p => ({
            ...p,
            [poiId]: {
              progress: res.data.progress || 0,
              message: res.data.message || 'En cours...'
            }
          }));
          attempts++;
          setTimeout(poll, 1000);
        }
      } catch (e) {
        attempts++;
        setTimeout(poll, 2000);
      }
    };
    
    poll();
  }, []);

  // Open duration selection dialog
  const openDurationSelector = (poi) => {
    setDurationDialog({ open: true, poiId: poi.poi_id, poi });
  };

  // Start generation with selected duration
  const handleStartGeneration = async (poiId, audience, mode = 'standard') => {
    setDurationDialog({ open: false, poiId: null, poi: null });
    
    setGeneratingPois(p => ({
      ...p,
      [poiId]: { progress: 5, message: 'Démarrage...' }
    }));
    
    try {
      const lang = user?.language || 'fr';
      await startAudioGeneration(poiId, lang, mode, audience);
      pollGenerationStatus(poiId);
    } catch (e) {
      setGeneratingPois(p => {
        const copy = { ...p };
        delete copy[poiId];
        return copy;
      });
      toast.error('Erreur démarrage génération');
    }
  };

  // Generate kids audio (always short)
  const handleGenerateKids = async (poiId) => {
    await handleStartGeneration(poiId, 'kids', 'short');
  };

  const handlePlayAudio = (poiId) => {
    const data = audioData[poiId];
    const poi = pois.find(p => p.poi_id === poiId);
    
    if (!data?.audioUrl) {
      toast.error('Pas d\'audio disponible');
      return;
    }
    
    setPlayerData({
      audioUrl: data.audioUrl,
      title: poi?.title || 'Guide Audio',
      subtitle: poi?.location || '',
      isImmersive: data.hasAmbiance || data.hasTransitions
    });
  };

  const handleClosePlayer = () => {
    setPlayerData(null);
  };

  // Archive a POI (soft delete)
  const handleArchivePoi = async (poiId) => {
    try {
      await api.put(`/poi/${poiId}/archive`);
      toast.success('Guide archivé');
      await loadPois();
      // Remove from audioData
      setAudioData(prev => {
        const copy = { ...prev };
        delete copy[poiId];
        return copy;
      });
    } catch (e) {
      toast.error('Erreur archivage');
    }
  };

  // Restore an archived POI
  const handleRestorePoi = async (poiId) => {
    try {
      await api.put(`/poi/${poiId}/restore`);
      toast.success('Guide restauré');
      await loadPois();
    } catch (e) {
      toast.error('Erreur restauration');
    }
  };

  // Delete a POI permanently
  const handleDeletePoi = async (poiId) => {
    try {
      await api.delete(`/poi/${poiId}`);
      toast.success('Guide supprimé définitivement');
      setDeleteDialog({ open: false, poiId: null, title: '' });
      await loadPois();
      // Remove from audioData
      setAudioData(prev => {
        const copy = { ...prev };
        delete copy[poiId];
        return copy;
      });
    } catch (e) {
      toast.error('Erreur suppression');
    }
  };

  const handleAddGuide = async () => {
    if (!newGuide.title || !newGuide.location) {
      toast.error('Titre et lieu requis');
      return;
    }
    
    setAddLoading(true);
    try {
      const poiId = `poi_${Date.now()}`;
      await api.post(`/trips/${tripId}/poi`, {
        poi_id: poiId,
        title: newGuide.title,
        location: newGuide.location,
        category: newGuide.category,
        description: newGuide.description
      });
      
      await loadPois();
      setAddOpen(false);
      setNewGuide({ title: '', location: '', category: 'monument', description: '' });
      toast.success('Lieu ajouté !');
      
    } catch (e) {
      toast.error('Erreur création');
    }
    setAddLoading(false);
  };

  // Check if POI has enough info for long audio
  const canHaveLongAudio = (poi) => {
    const descLen = (poi.description || '').length;
    const isRichCategory = ['museum', 'monument', 'palace', 'church'].includes(poi.category);
    return descLen > 50 || isRichCategory;
  };

  return (
    <div className="space-y-4" data-testid="visit-companion">
      {/* Floating Audio Player */}
      {playerData && (
        <AudioPlayer
          audioUrl={playerData.audioUrl}
          title={playerData.title}
          subtitle={playerData.subtitle}
          isImmersive={playerData.isImmersive}
          onClose={handleClosePlayer}
          onEnded={handleClosePlayer}
        />
      )}
      
      {/* Duration Selection Dialog */}
      <Dialog open={durationDialog.open} onOpenChange={(open) => setDurationDialog({ ...durationDialog, open })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">Choisir la durée</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-4">
            <p className="text-sm text-center text-[#64748B] mb-4">
              {durationDialog.poi?.title}
            </p>
            
            {/* Short */}
            <button
              onClick={() => handleStartGeneration(durationDialog.poiId, 'adult', 'short')}
              className="w-full p-4 rounded-xl border border-[#E2E8F0] hover:border-[#3A86FF] hover:bg-blue-50 transition-all flex items-center gap-4 group"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-2xl">
                ⚡
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-[#1E293B]">Court</p>
                <p className="text-xs text-[#64748B]">~3 minutes • L'essentiel</p>
              </div>
              <Timer className="w-5 h-5 text-[#94A3B8] group-hover:text-[#3A86FF]" />
            </button>
            
            {/* Standard */}
            <button
              onClick={() => handleStartGeneration(durationDialog.poiId, 'adult', 'standard')}
              className="w-full p-4 rounded-xl border-2 border-[#3A86FF] bg-blue-50 transition-all flex items-center gap-4"
            >
              <div className="w-12 h-12 rounded-xl bg-[#3A86FF] flex items-center justify-center text-2xl text-white">
                📖
              </div>
              <div className="text-left flex-1">
                <p className="font-semibold text-[#1E293B]">Moyen</p>
                <p className="text-xs text-[#64748B]">~5 minutes • Recommandé</p>
              </div>
              <Badge className="bg-[#3A86FF] text-white text-[10px]">Recommandé</Badge>
            </button>
            
            {/* Long - only if rich content */}
            {canHaveLongAudio(durationDialog.poi || {}) && (
              <button
                onClick={() => handleStartGeneration(durationDialog.poiId, 'adult', 'deep')}
                className="w-full p-4 rounded-xl border border-[#E2E8F0] hover:border-purple-400 hover:bg-purple-50 transition-all flex items-center gap-4 group"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center text-2xl">
                  🎓
                </div>
                <div className="text-left flex-1">
                  <p className="font-semibold text-[#1E293B]">Complet</p>
                  <p className="text-xs text-[#64748B]">7-10 minutes • Détaillé</p>
                </div>
                <Timer className="w-5 h-5 text-[#94A3B8] group-hover:text-purple-500" />
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Tab selector */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2 bg-[#F1F5F9] p-1 rounded-xl w-fit">
          <button onClick={() => setTab('guides')} className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${tab === 'guides' ? 'bg-white shadow-sm text-[#1E293B]' : 'text-[#64748B]'}`}>
            <Compass className="w-3.5 h-3.5 inline mr-1" /> Guides Audio
          </button>
          <button onClick={() => setTab('quiz')} className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${tab === 'quiz' ? 'bg-white shadow-sm text-[#1E293B]' : 'text-[#64748B]'}`}>
            <Star className="w-3.5 h-3.5 inline mr-1" /> Quiz Enfants
          </button>
        </div>
        
        {tab === 'guides' && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white text-xs h-9" data-testid="add-guide-btn">
                <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un lieu
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Ajouter un lieu</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 rounded-xl p-3">
                  <div className="flex items-center gap-2 text-blue-700 text-sm font-medium mb-1">
                    <Music className="w-4 h-4" />
                    Audio Immersif
                  </div>
                  <p className="text-xs text-blue-600">Voix HD + ambiance sonore + transitions</p>
                </div>
                
                <div>
                  <Label className="text-sm">Nom du lieu *</Label>
                  <Input 
                    value={newGuide.title} 
                    onChange={e => setNewGuide({...newGuide, title: e.target.value})}
                    placeholder="Ex: Tour Eiffel, Musée du Louvre..."
                    className="mt-1"
                    data-testid="new-guide-title"
                  />
                </div>
                <div>
                  <Label className="text-sm">Adresse / Lieu *</Label>
                  <Input 
                    value={newGuide.location} 
                    onChange={e => setNewGuide({...newGuide, location: e.target.value})}
                    placeholder="Ex: Paris, France"
                    className="mt-1"
                    data-testid="new-guide-location"
                  />
                </div>
                <div>
                  <Label className="text-sm">Catégorie</Label>
                  <Select value={newGuide.category} onValueChange={v => setNewGuide({...newGuide, category: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monument">Monument</SelectItem>
                      <SelectItem value="museum">Musée</SelectItem>
                      <SelectItem value="park">Parc / Nature</SelectItem>
                      <SelectItem value="church">Église / Cathédrale</SelectItem>
                      <SelectItem value="palace">Palais / Château</SelectItem>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="beach">Plage</SelectItem>
                      <SelectItem value="market">Marché</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Description (optionnel)</Label>
                  <Input 
                    value={newGuide.description} 
                    onChange={e => setNewGuide({...newGuide, description: e.target.value})}
                    placeholder="Quelques infos sur le lieu..."
                    className="mt-1"
                  />
                </div>
                <Button 
                  onClick={handleAddGuide} 
                  disabled={addLoading}
                  className="w-full rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white"
                  data-testid="save-new-guide-btn"
                >
                  {addLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Ajouter le lieu
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {tab === 'guides' && (
        <>
          {/* Archive toggle */}
          {pois.some(p => p.is_archived) && (
            <div className="flex items-center gap-2 mb-2">
              <button 
                onClick={() => setShowArchived(!showArchived)}
                className={`text-xs px-3 py-1 rounded-lg transition-all ${showArchived ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-500'}`}
              >
                <Archive className="w-3 h-3 inline mr-1" />
                {showArchived ? 'Masquer archivés' : `Voir archivés (${pois.filter(p => p.is_archived).length})`}
              </button>
            </div>
          )}
          
          {loading ? (
            <div className="widget-card p-10 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#3A86FF] mx-auto" />
              <p className="text-sm text-[#94A3B8] mt-3">Chargement des guides...</p>
            </div>
          ) : pois.filter(p => showArchived || !p.is_archived).length === 0 ? (
            <div className="widget-card p-10 text-center">
              <Compass className="w-10 h-10 text-[#CBD5E1] mx-auto mb-3" strokeWidth={1.2} />
              <p className="text-sm text-[#94A3B8] mb-4">Aucun guide audio pour le moment</p>
              <Button onClick={() => setAddOpen(true)} variant="outline" className="rounded-xl">
                <Plus className="w-4 h-4 mr-2" /> Créer votre premier guide
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {pois.filter(p => showArchived || !p.is_archived).map((poi, i) => {
                const data = audioData[poi.poi_id];
                const generating = generatingPois[poi.poi_id];
                const hasAudio = data?.audioUrl;
                const isArchived = poi.is_archived;
                
                return (
                  <div 
                    key={poi.poi_id || i} 
                    className={`widget-card p-5 group transition-all ${isArchived ? 'opacity-60 border-gray-300' : hasAudio ? 'border-green-200' : ''}`} 
                    data-testid={`poi-${poi.poi_id || i}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Badge className="text-[9px] rounded-md border-0" style={{ backgroundColor: `${CAT_COLORS[poi.category] || '#94A3B8'}15`, color: CAT_COLORS[poi.category] || '#94A3B8' }}>
                          {poi.category || 'lieu'}
                        </Badge>
                        {isArchived && (
                          <Badge variant="outline" className="text-[9px] text-gray-500">
                            <Archive className="w-2.5 h-2.5 mr-1" /> Archivé
                          </Badge>
                        )}
                      </div>
                      
                      {/* Actions menu */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="w-4 h-4 text-gray-400" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          {isArchived ? (
                            <>
                              <DropdownMenuItem onClick={() => handleRestorePoi(poi.poi_id)} className="text-green-600">
                                <Archive className="w-4 h-4 mr-2" /> Restaurer
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => setDeleteDialog({ open: true, poiId: poi.poi_id, title: poi.title })}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem onClick={() => handleArchivePoi(poi.poi_id)} className="text-gray-600">
                              <Archive className="w-4 h-4 mr-2" /> Archiver
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {data?.hasAmbiance && !isArchived && (
                      <Badge className="text-[9px] rounded-md border-0 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 mb-2">
                        <Music className="w-2.5 h-2.5 mr-1" /> Immersif
                      </Badge>
                    )}
                    
                    <h3 className="font-semibold text-[#1E293B] text-sm mb-1">{poi.title}</h3>
                    <p className="text-[11px] text-[#64748B] flex items-center gap-1 mb-3">
                      <MapPin className="w-3 h-3" /> {poi.location}
                    </p>
                    
                    {/* Archived state - show restore button */}
                    {isArchived ? (
                      <Button 
                        onClick={() => handleRestorePoi(poi.poi_id)}
                        variant="outline"
                        className="w-full rounded-xl text-xs h-9"
                      >
                        <Archive className="w-3.5 h-3.5 mr-1" /> Restaurer
                      </Button>
                    ) : (
                      <>
                        {/* Generating state */}
                        {generating && (
                          <div className="mb-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl border border-blue-100">
                            <div className="flex items-center gap-2 mb-2">
                              <Loader2 className="w-4 h-4 text-[#3A86FF] animate-spin" />
                              <span className="text-xs text-[#3A86FF] font-medium">{generating.message}</span>
                            </div>
                            <Progress value={generating.progress} className="h-1.5" />
                            <p className="text-[10px] text-[#64748B] mt-1">{generating.progress}% complété</p>
                          </div>
                        )}
                    
                    {/* Audio ready state */}
                    {hasAudio && !generating && (
                      <div className={`mb-3 p-2 rounded-lg border flex items-center gap-2 ${data.hasAmbiance ? 'bg-purple-50 border-purple-100' : 'bg-green-50 border-green-100'}`}>
                        {data.hasAmbiance ? (
                          <>
                            <Music className="w-3.5 h-3.5 text-purple-500" />
                            <span className="text-xs text-purple-700">Audio immersif</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span className="text-xs text-green-700">Audio prêt</span>
                          </>
                        )}
                        {data.mode && (
                          <Badge variant="outline" className="text-[9px] ml-auto">
                            {data.mode === 'short' ? '~3min' : data.mode === 'deep' ? '7-10min' : '~5min'}
                          </Badge>
                        )}
                      </div>
                    )}
                    
                    {/* Actions */}
                    <div className="space-y-2">
                      {hasAudio ? (
                        <Button 
                          onClick={() => handlePlayAudio(poi.poi_id)}
                          className="w-full rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white text-xs h-9"
                        >
                          <Play className="w-3.5 h-3.5 mr-1" /> Écouter
                        </Button>
                      ) : !generating ? (
                        <div className="grid grid-cols-2 gap-2">
                          {/* Adult button - opens duration selector */}
                          <Button 
                            onClick={() => openDurationSelector(poi)}
                            className="rounded-xl bg-[#3A86FF] hover:bg-[#2563EB] text-white text-xs h-10"
                          >
                            <User className="w-3.5 h-3.5 mr-1" /> Adulte
                          </Button>
                          
                          {/* Kids button - always short */}
                          <Button 
                            onClick={() => handleGenerateKids(poi.poi_id)}
                            className="rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white text-xs h-10"
                          >
                            <Baby className="w-3.5 h-3.5 mr-1" /> Enfants
                          </Button>
                        </div>
                      ) : (
                        <Button disabled className="w-full rounded-xl text-xs h-9">
                          <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Génération...
                        </Button>
                      )}
                      
                      {/* Regenerate buttons when audio exists */}
                      {hasAudio && !generating && (
                        <div className="grid grid-cols-2 gap-2">
                          <Button 
                            onClick={() => openDurationSelector(poi)}
                            variant="outline"
                            className="rounded-xl text-xs h-8 text-[#64748B]"
                          >
                            <Wand2 className="w-3 h-3 mr-1" /> Régénérer
                          </Button>
                          <Button 
                            onClick={() => handleGenerateKids(poi.poi_id)}
                            variant="outline"
                            className="rounded-xl text-xs h-8 border-amber-200 text-amber-600"
                          >
                            <Baby className="w-3 h-3 mr-1" /> Enfants
                          </Button>
                        </div>
                      )}
                    </div>
                    
                    {/* Text preview */}
                    {data?.text && (
                      <details className="mt-3">
                        <summary className="text-[10px] text-[#94A3B8] cursor-pointer hover:text-[#64748B]">
                          Voir le texte du guide
                        </summary>
                        <p className="text-[11px] text-[#64748B] mt-2 max-h-32 overflow-y-auto bg-[#F8FAFC] rounded-lg p-2">
                          {data.text.slice(0, 500)}...
                        </p>
                      </details>
                    )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer définitivement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>"{deleteDialog.title}"</strong> ? 
              Cette action est irréversible et supprimera également tous les guides audio associés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeletePoi(deleteDialog.poiId)}
              className="bg-red-600 hover:bg-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {tab === 'quiz' && (
        <div className="widget-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-[#1E293B]">Quiz Découverte</h3>
            <Button onClick={handleGenerateQuiz} disabled={quizLoading} className="rounded-xl bg-[#F59E0B] hover:bg-[#D97706] text-white text-xs h-9">
              {quizLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              {quiz.length > 0 ? 'Nouveau quiz' : 'Générer un quiz'}
            </Button>
          </div>
          
          {quiz.length === 0 ? (
            <p className="text-sm text-[#94A3B8] text-center py-8">
              Générez un quiz pour tester les connaissances des enfants sur la destination !
            </p>
          ) : (
            <div className="space-y-4">
              {quiz.map((q, idx) => {
                const ans = answers[q.quiz_id];
                return (
                  <div key={q.quiz_id || idx} className="p-4 bg-[#F8FAFC] rounded-xl">
                    <p className="font-medium text-sm text-[#1E293B] mb-3">{idx + 1}. {q.question}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {q.options?.map((opt, oi) => (
                        <button 
                          key={oi}
                          onClick={() => handleAnswer(q.quiz_id, opt, q.correct_answer)}
                          disabled={!!ans}
                          className={`p-2 rounded-lg text-xs text-left transition-all ${
                            ans 
                              ? opt === q.correct_answer 
                                ? 'bg-green-100 text-green-700 border border-green-200' 
                                : ans.answer === opt 
                                  ? 'bg-red-100 text-red-700 border border-red-200'
                                  : 'bg-white text-[#64748B] border border-[#E2E8F0]'
                              : 'bg-white text-[#1E293B] border border-[#E2E8F0] hover:border-[#3A86FF] hover:bg-blue-50'
                          }`}
                        >
                          {opt}
                          {ans && opt === q.correct_answer && <CheckCircle className="w-3 h-3 inline ml-1" />}
                          {ans && ans.answer === opt && opt !== q.correct_answer && <XCircle className="w-3 h-3 inline ml-1" />}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              
              {Object.keys(answers).length === quiz.length && quiz.length > 0 && (
                <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl text-center">
                  <p className="font-semibold text-amber-700">
                    Score: {Object.values(answers).filter(a => a.isCorrect).length} / {quiz.length}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VisitCompanion;
